import React, { useState, useEffect, useRef } from 'react';
import { getObstacleIcon, getArpIcon } from '../../utils/markers';
import L from 'leaflet';
import * as turf from '@turf/turf';
import 'leaflet/dist/leaflet.css';
import { T } from '../../assets/theme';
import { getGeoBorders } from '../../api/client';
import MapMeasureTool from '../../components/MapMeasureTool';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const ARP_ICON = '#3a8ac4';
const CNS_ICON = '#f5a623';

// SVG icon helpers
function svgIcon(url, size = 28) {
    const h = Math.round(size / 2);
    return L.divIcon({
        className: '',
        html: `<img src="${url}" width="${size}" height="${size}" style="display:block;">`,
        iconSize: [size, size], iconAnchor: [h, h], popupAnchor: [0, -h],
    });
}
function cnsIconUrl(type) {
    const t = (type || '').toUpperCase();
    if (t.includes('VORTAC')) return '/symbols/VORTAC.svg';
    if (t.includes('VOR') || t.includes('CVOR') || t.includes('DVOR')) return '/symbols/VOR.svg';
    if (t.includes('ILS LOC') || t.startsWith('LOC')) return '/symbols/LOC.svg';
    if (t.includes('ILS GP') || t.includes('ILS DME') || t.includes('GP')) return '/symbols/GPDME.svg';
    if (t.includes('DME')) return '/symbols/DMEVOR.svg';
    return null;
}

const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OpenStreetMap' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

function circleIcon(color, size = 14) {
    return L.divIcon({
        className: '',
        html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2.5px solid #fff;box-shadow:0 0 8px ${color}99;cursor:pointer;"></div>`,
        iconSize: [size, size], iconAnchor: [size / 2, size / 2],
    });
}

const DEG = Math.PI / 180;
function haversine(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const dLat = (lat2 - lat1) * DEG, dLon = (lon2 - lon1) * DEG;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * DEG) * Math.cos(lat2 * DEG) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function bearing(lat1, lon1, lat2, lon2) {
    const dLon = (lon2 - lon1) * DEG;
    const y = Math.sin(dLon) * Math.cos(lat2 * DEG);
    const x = Math.cos(lat1 * DEG) * Math.sin(lat2 * DEG) - Math.sin(lat1 * DEG) * Math.cos(lat2 * DEG) * Math.cos(dLon);
    return ((Math.atan2(y, x) / DEG) + 360) % 360;
}

function braParams(type) {
    const t = (type || '').toUpperCase();
    if (t.includes('CVOR') || t.includes('VOR')) return { dir: false, cylR: 300, alpha: 2, coneR: 10000, j: 600, cylH: 90 };
    if (t.includes('DVOR')) return { dir: false, cylR: 600, alpha: 2, coneR: 12000, j: 1000, cylH: 90 };
    if (t.includes('DME')) return { dir: false, cylR: 300, alpha: 2, coneR: 10000, j: 600, cylH: 90 };
    if (t.includes('NDB')) return { dir: false, cylR: 300, alpha: 2, coneR: 5000, j: 300, cylH: 30 };
    return { dir: false, cylR: 300, alpha: 2, coneR: 5000, j: 300, cylH: 30 };
}

function computeBRA(cns, obstacle) {
    const cLat = parseFloat(cns.lat), cLon = parseFloat(cns.lon);
    const oLat = parseFloat(obstacle.latitude), oLon = parseFloat(obstacle.longitude);
    if (!cLat || !cLon || !oLat || !oLon) return null;
    const params = braParams(cns.type);
    const distM = haversine(cLat, cLon, oLat, oLon);
    const brg = bearing(cLat, cLon, oLat, oLon);
    const htol = parseFloat(obstacle.h_tolerance || 0);
    const effDist = Math.max(0, distM - htol);
    const cnsElevM = parseFloat(cns.elev_ft || 0) * 0.3048;
    const obstElevM = parseFloat(obstacle.elevation_m || 0);
    const { cylR, alpha, coneR, j, cylH } = params;
    let surface = null, braElev = null;
    if (effDist <= j) { surface = 'Inner/Outer Cylinder'; braElev = cnsElevM + cylH; }
    else if (effDist <= coneR) { braElev = cnsElevM + cylH + Math.tan(alpha * DEG) * (effDist - j); surface = 'Cone'; }
    else { surface = 'Outside'; braElev = null; }
    const infringed = braElev !== null ? (obstElevM > braElev ? 'YES' : 'No') : 'N/A';
    return { distM: Math.round(effDist), brg: brg.toFixed(1), surface, braElev: braElev?.toFixed(2) || '—', obstElev: obstElevM, infringed };
}

function BRAPanel({ cns, obstacle, onClose }) {
    if (!obstacle || !cns) return null;
    const res = computeBRA(cns, obstacle);
    if (!res) return null;
    const iColor = res.infringed === 'YES' ? T.danger : T.safe;
    const rows = [
        ['CNS Ident', cns.ident || '—'], ['Type', cns.type],
        ['Distance', res.distM + ' m'], ['Bearing', res.brg + '°'],
        ['Surface', res.surface], ['BRA Elevation', res.braElev + ' m'],
        ['Obstacle Elev', res.obstElev + ' m'], ['Infringed', res.infringed],
    ];
    return (
        <div style={{ background: T.bg1, borderTop: `2px solid ${CNS_ICON}44`, padding: '12px 20px', maxHeight: 240, overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: CNS_ICON }}>📡 BRA — {cns.ident} vs {obstacle.name}</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: 16 }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '4px 16px', fontSize: 11 }}>
                {rows.map(([k, v]) => (
                    <div key={k}>
                        <div style={{ color: T.muted, fontSize: 10 }}>{k}</div>
                        <div style={{ color: k === 'Infringed' ? iColor : T.white, fontWeight: k === 'Infringed' ? 800 : 400 }}>{v}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function ObstaclePreview({ project, nearby, loading, showObstacles = true, showObsLabels, showArpLabels, onObstacleContextMenu, onMapReady, onAerodromeContextMenu }) {
    const mapRef = useRef(null), mapInst = useRef(null), tileRef = useRef(null), layersRef = useRef([]);
    const [mapMode, setMapMode] = useState('street');
    const [selectedCns, setSelectedCns] = useState(null);
    const [braObstacle, setBraObstacle] = useState(null);
    const [listTab, setListTab] = useState('arps');
    const [geoBorders, setGeoBorders] = useState([]);
    const [mapState, setMapState] = useState(null);
    const [capturing, setCapturing] = useState(false);
    const [exportingBuffers, setExportingBuffers] = useState(false);

    const handleExportBuffersKmz = async () => {
        if (!project?.id) return;
        setExportingBuffers(true);
        try {
            const resp = await fetch(`${API_BASE}/projects/${project.id}/obstacles/buffers-kmz`);
            if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `project_${project.id}_obstacle_buffers.kmz`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export buffer zones KMZ:', err);
            alert('Failed to export buffer zones KMZ. Please check your network connection and try again.');
        } finally {
            setExportingBuffers(false);
        }
    };

    // Manual canvas capture — same approach as OLS page which avoids the
    // northeast coordinate shift that html2canvas produces on Leaflet containers.
    const handleCapture = async () => {
        if (!mapRef.current || !project?.id) return;
        setCapturing(true);
        try {
            const container = mapRef.current;
            const containerRect = container.getBoundingClientRect();
            const w = Math.round(containerRect.width);
            const h = Math.round(containerRect.height);

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#07111e';
            ctx.fillRect(0, 0, w, h);

            // 1. Draw base map tiles
            const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
            await Promise.all(tileEls.map(async tile => {
                if (!tile.src) return;
                const tRect = tile.getBoundingClientRect();
                const tx = Math.floor(tRect.left - containerRect.left);
                const ty = Math.floor(tRect.top - containerRect.top);
                const tw = Math.ceil(tRect.width) + 1;
                const th = Math.ceil(tRect.height) + 1;
                try {
                    const resp = await fetch(tile.src, { mode: 'cors' });
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    await new Promise(resolve => {
                        const img = new Image();
                        img.onload = () => { ctx.drawImage(img, tx, ty, tw, th); URL.revokeObjectURL(url); resolve(); };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                        img.src = url;
                    });
                } catch { /* skip broken tiles */ }
            }));

            // 2. Draw SVG overlay (circles, polygons, geo borders)
            const svgEl = container.querySelector('.leaflet-overlay-pane svg');
            if (svgEl) {
                const svgRect = svgEl.getBoundingClientRect();
                const sx = Math.round(svgRect.left - containerRect.left);
                const sy = Math.round(svgRect.top - containerRect.top);
                const clone = svgEl.cloneNode(true);
                clone.style.transform = '';
                clone.style.left = '0';
                clone.style.top = '0';
                const svgStr = new XMLSerializer().serializeToString(clone);
                const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => { ctx.drawImage(img, sx, sy, Math.round(svgRect.width), Math.round(svgRect.height)); URL.revokeObjectURL(url); resolve(); };
                    img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    img.src = url;
                });
            }

            // 3. Draw HTML markers (SVG icon images)
            const markerEls = [...container.querySelectorAll('.leaflet-marker-icon')];
            for (const marker of markerEls) {
                const mRect = marker.getBoundingClientRect();
                const x = Math.round(mRect.left - containerRect.left);
                const y = Math.round(mRect.top - containerRect.top);
                const img = marker.querySelector('img');
                if (img && img.complete && img.naturalWidth) {
                    try { ctx.drawImage(img, x, y, Math.round(mRect.width), Math.round(mRect.height)); } catch { }
                }
            }

            // 4. Draw div-icon labels (ICAO text labels above ARP markers)
            // Only target leaf-level divs (no div children) so we don't draw
            // the same text twice from parent wrapper + child label divs.
            const divIconEls = [...container.querySelectorAll('.leaflet-marker-icon div')];
            for (const el of divIconEls) {
                // Skip any div that contains child div elements — its textContent
                // would just be the concatenation of its children, causing duplicates.
                if (el.querySelector('div')) continue;
                const eRect = el.getBoundingClientRect();
                const x = Math.round(eRect.left - containerRect.left);
                const y = Math.round(eRect.top - containerRect.top);
                const text = el.textContent?.trim();
                if (text) {
                    ctx.save();
                    ctx.font = 'bold 11px sans-serif';
                    ctx.fillStyle = el.style.color || '#ff9800';
                    ctx.strokeStyle = 'rgba(0,0,0,0.7)';
                    ctx.lineWidth = 3;
                    ctx.strokeText(text, x, y + 10);
                    ctx.fillText(text, x, y + 10);
                    ctx.restore();
                }
            }

            const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
            const res = await fetch(`${API_BASE}/ols-surfaces/PROJECT_${project.id}/screenshot?type=preview&project_id=${project.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image: dataUrl }),
            });
            if (res.ok) {
                alert('Preview map captured and saved to project report!');
            } else {
                alert('Server rejected screenshot. Please try again.');
            }
        } catch (err) {
            console.error('Capture failed:', err);
            alert('Failed to capture map preview.');
        } finally {
            setCapturing(false);
        }
    };
  
      useEffect(() => {
          getGeoBorders(false, true).then(res => {
              if (res.data) setGeoBorders(res.data);
          }).catch(err => console.error("Failed to load geo borders", err));
      }, []);
  

    const obstacles = project?.obstacles || [];
    const arps = nearby?.nearby_aerodromes || [];
    const cns = nearby?.nearby_cns || [];

    useEffect(() => {
        if (!mapRef.current || mapInst.current) return;
        mapInst.current = L.map(mapRef.current, {
            center: [-28.5, 25.0],
            zoom: 6,
            zoomDelta: 0.7,
            zoomSnap: 0.7
        });
        tileRef.current = L.tileLayer(TILE_LAYERS[mapMode].url, { attribution: TILE_LAYERS[mapMode].attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        setTimeout(() => { mapInst.current?.invalidateSize(); }, 0);
        setMapState(mapInst.current);
        onMapReady?.(mapInst.current);
        return () => { mapInst.current?.remove(); mapInst.current = null; setMapState(null); onMapReady?.(null); };
    }, []);

    useEffect(() => {
        if (!mapInst.current) return;
        if (tileRef.current) mapInst.current.removeLayer(tileRef.current);
        const l = TILE_LAYERS[mapMode];
        tileRef.current = L.tileLayer(l.url, { attribution: l.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    useEffect(() => {
        if (!mapInst.current || loading) return;
        layersRef.current.forEach(l => mapInst.current.removeLayer(l));
        layersRef.current = [];
        const bounds = [];

        const validObs = obstacles.filter(o => o.latitude && o.longitude);
        getObstaclesToRender(validObs, project?.id, showObstacles).forEach(o => {
            const ll = [o.latitude, o.longitude];
            bounds.push(ll);
            const obsIcon = getObstacleIcon(L, o, undefined, showObsLabels);
            const m = L.marker(ll, { icon: obsIcon })
                .bindPopup(`<b>${o.name}</b><br/>ID: ${o.id}<br/>ELEVATION : ${o.elevation_m ?? '—'} m<br/>SURFACE: —<br/>PENETRATION: —`);
            if (onObstacleContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(mapInst.current); layersRef.current.push(m);
            if (o.h_tolerance) {
                const halo = L.circle(ll, { radius: o.h_tolerance, color: '#e84040', weight: 1.5, fillOpacity: 0.06, dashArray: '5 4' });
                halo.addTo(mapInst.current); layersRef.current.push(halo);
            }
            if (!braObstacle) setBraObstacle(o);
        });

        addHullPolygonsToMap(mapInst.current, validObs, project?.id, layersRef);

        if (validObs.length > 0) {
            const obsCoords = validObs.map(o => [o.longitude, o.latitude]);
            let baseGeo;
            if (obsCoords.length === 1) {
                baseGeo = turf.point(obsCoords[0]);
            } else if (obsCoords.length === 2) {
                baseGeo = turf.lineString(obsCoords);
            } else {
                baseGeo = turf.convex(turf.featureCollection(obsCoords.map(c => turf.point(c))));
                if (!baseGeo) {
                    // Fallback to simple polygon if convex hull fails (e.g. collinear points)
                    const closed = [...obsCoords, obsCoords[0]];
                    baseGeo = turf.polygon([closed]);
                }
            }

            const buffers = [
                { id: 'ols', dist: 15, color: '#2ec98b', label: '15km OLS/CNS' },
                { id: 'pansops', dist: 55.56, color: '#f5a623', label: '30NM PANS-OPS' },
                { id: 'rmac', dist: 101.86, color: '#9b59b6', label: '55NM RMAC' }
            ];

            let prevBufferGeo = null;

            buffers.forEach(b => {
                try {
                    const buff = turf.buffer(baseGeo, b.dist, { units: 'kilometers', steps: 64 });
                    let displayGeo = buff;

                    if (prevBufferGeo) {
                        const diffGeo = turf.difference(turf.featureCollection([buff, prevBufferGeo]));
                        if (diffGeo) {
                            displayGeo = diffGeo;
                        }
                    }

                    const layer = L.geoJSON(displayGeo, {
                        style: { color: b.color, weight: 1.5, fillOpacity: 0.03, dashArray: '8 5' }
                    }).bindTooltip(b.label, { sticky: true });
                    layer.addTo(mapInst.current);
                    layersRef.current.push(layer);

                    prevBufferGeo = buff;
                } catch (e) {
                    console.warn(`Buffer failed for ${b.dist}km`, e);
                }
            });
        }

        arps.filter(a => a.lat && a.lon).forEach(a => {
            const ll = [a.lat, a.lon]; bounds.push(ll);
            const m = L.marker(ll, { icon: getArpIcon(L, a, showArpLabels) })
                .bindPopup(`<b>✈ ${a.icao}</b><br/>${a.name}<br/>Dist: ${a.distance_km} km`);
            if (onAerodromeContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onAerodromeContextMenu(a, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(mapInst.current); layersRef.current.push(m);
        });

        cns.filter(c => c.lat && c.lon).forEach(c => {
            const ll = [c.lat, c.lon]; bounds.push(ll);
            const iconUrl = cnsIconUrl(c.type || c.feattype);
            const icon = iconUrl
                ? svgIcon(iconUrl, 26)
                : L.divIcon({ className: '', html: `<div style="width:12px;height:12px;border-radius:50%;background:${CNS_ICON};border:2.5px solid #fff;box-shadow:0 0 8px ${CNS_ICON}99;cursor:pointer;"></div>`, iconSize: [12, 12], iconAnchor: [6, 6] });
            const m = L.marker(ll, { icon })
                .bindPopup(`<b>${c.ident || c.name}</b><br/>${c.type}<br/>Dist: ${c.distance_km} km`)
                .on('click', () => setSelectedCns(c));
            m.addTo(mapInst.current); layersRef.current.push(m);
        });


        geoBorders.forEach(b => {
             if (!b.geometry) return;
             const color = b.is_active ? '#2ec98b' : '#888888';
             const layer = L.geoJSON(b.geometry, {
                 interactive: false,
                 style: {
                     color: color,
                     weight: b.is_active ? 2 : 1,
                     fillOpacity: b.is_active ? 0.05 : 0.01,
                     dashArray: b.is_active ? '5 5' : ''
                 }
             });
             layer.addTo(mapInst.current);
             layersRef.current.push(layer);

             if (b.is_active && bounds.length === 0) {
                 const bnds = layer.getBounds();
                 if (bnds.isValid()) {
                     bounds.push(bnds.getSouthWest());
                     bounds.push(bnds.getNorthEast());
                 }
             }
         });

        if (bounds.length > 0) mapInst.current.fitBounds(L.latLngBounds(bounds), { padding: [60, 60], maxZoom: 13 });
    }, [project, nearby, loading, geoBorders, showObstacles, showObsLabels, showArpLabels, onObstacleContextMenu, onAerodromeContextMenu]); // eslint-disable-line

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Side list */}
                <div style={{ width: 280, flexShrink: 0, background: T.bg1, backdropFilter: 'blur(var(--blur-md))', WebkitBackdropFilter: 'blur(var(--blur-md))', borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {/* Obstacle selector for BRA */}
                    {obstacles.length > 1 && (
                        <div style={{ padding: '8px 12px', borderBottom: `1px solid ${T.border}` }}>
                            <div style={{ fontSize: 10, color: T.muted, marginBottom: 4 }}>BRA AGAINST OBSTACLE</div>
                            <select value={braObstacle?.id || ''} onChange={e => setBraObstacle(obstacles.find(o => o.id === Number(e.target.value)))}
                                style={{ width: '100%', background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6, padding: '5px 8px', color: T.white, fontSize: 11, outline: 'none' }}>
                                {obstacles.filter(o => o.latitude && o.longitude).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                            </select>
                        </div>
                    )}
                    {/* ARP / CNS tabs */}
                    <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}` }}>
                        {[{ k: 'arps', l: `✈ ARP (${arps.length})` }, { k: 'cns', l: `📡 CNS (${cns.length})` }].map(t => (
                            <button key={t.k} onClick={() => setListTab(t.k)} style={{ flex: 1, padding: '8px 4px', background: 'none', border: 'none', borderBottom: listTab === t.k ? `2px solid ${T.blue}` : '2px solid transparent', color: listTab === t.k ? T.blue : T.muted, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{t.l}</button>
                        ))}
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {listTab === 'arps' ? arps.map((a, i) => (
                            <div key={i} style={{ padding: '8px 12px', borderBottom: `1px solid ${T.border}11` }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: T.blue }}>{a.icao}</span>
                                    <span style={{ fontSize: 10, color: T.safe }}>{a.distance_km} km</span>
                                </div>
                                <div style={{ fontSize: 11, color: T.white }}>{a.name}</div>
                            </div>
                        )) : cns.map((c, i) => {
                            const isSel = selectedCns?.navaidid === c.navaidid;
                            return (
                                <div key={i} onClick={() => setSelectedCns(isSel ? null : c)} style={{ padding: '8px 12px', borderBottom: `1px solid ${T.border}11`, cursor: 'pointer', background: isSel ? 'rgba(245,166,35,0.08)' : 'transparent', borderLeft: isSel ? `3px solid ${CNS_ICON}` : '3px solid transparent' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: CNS_ICON }}>{c.ident || '—'}</span>
                                        <span style={{ fontSize: 10, color: T.safe }}>{c.distance_km} km</span>
                                    </div>
                                    <div style={{ fontSize: 11, color: T.white }}>{c.name}</div>
                                    <div style={{ fontSize: 10, color: T.muted }}>{c.type}</div>
                                </div>
                            );
                        })}
                    </div>
                    {/* Legend */}
                    <div style={{ padding: '8px 12px', borderTop: `1px solid ${T.border}` }}>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            {[{ color: '#e84040', label: 'Obstacle' }, { color: ARP_ICON, label: 'ARP' }, { color: CNS_ICON, label: 'CNS' }].map(l => (
                                <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: T.muted }}>
                                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: l.color }} /> {l.label}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Map */}
                <div style={{ flex: 1, position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', gap: 6, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 6, border: `1px solid ${T.border}`, backdropFilter: 'blur(8px)' }}>
                        {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                            <button key={key} onClick={() => setMapMode(key)} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: mapMode === key ? T.blue : 'transparent', color: mapMode === key ? T.white : T.muted, fontSize: 11, fontWeight: mapMode === key ? 700 : 400 }}>{layer.label}</button>
                        ))}
                        <div style={{ width: 1, background: T.border, margin: '2px 4px' }} />
                        <button
                            onClick={handleExportBuffersKmz}
                            disabled={exportingBuffers || !project?.id}
                            title="Download the 15km OLS/CNS, 30NM PANS-OPS and 55NM RMAC buffer rings as a KMZ for Google Earth"
                            style={{
                                padding: '6px 14px', borderRadius: 6, border: 'none',
                                cursor: (exportingBuffers || !project?.id) ? 'not-allowed' : 'pointer',
                                opacity: !project?.id ? 0.5 : 1,
                                background: '#9b59b6', color: '#FFFFFF',
                                fontSize: 12, fontWeight: 700,
                                display: 'flex', alignItems: 'center', gap: 6,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                            }}
                        >
                            {exportingBuffers ? 'Exporting...' : 'Buffer Zones KMZ'}
                        </button>
                        <button
                            onClick={handleCapture}
                            disabled={capturing}
                            style={{
                                padding: '6px 14px', borderRadius: 6, border: 'none',
                                cursor: capturing ? 'not-allowed' : 'pointer',
                                background: T.safe, color: '#FFFFFF',
                                fontSize: 12, fontWeight: 700,
                                display: 'flex', alignItems: 'center', gap: 6,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                            }}
                        >
                            {capturing ? 'Saving...' : 'Capture Screenshot'}
                        </button>
                    </div>
                    <MapMeasureTool map={mapState} top={52} tableOffset={0} />
                    <MapCoordinateViewer map={mapState} />
                    <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
                </div>
            </div>

            {/* BRA Panel */}
            {selectedCns && braObstacle && (
                <BRAPanel cns={selectedCns} obstacle={braObstacle} onClose={() => setSelectedCns(null)} />
            )}
        </div>
    );
}
